import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { BrandButton } from "@/components/PageHeader";
import { api, cedi, errMsg } from "@/lib/api";
import { FIXED_CATEGORIES } from "@/lib/categories";

const PricingChoice = ({ reprice, setReprice, target }) => (
  <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3 cursor-pointer" data-testid="move-legacy-reprice-row">
    <Switch checked={reprice} onCheckedChange={setReprice} data-testid="move-legacy-reprice-switch" />
    <span className="text-sm">
      <span className="font-semibold block">Reprice unpaid registrations</span>
      <span className="text-gray-500 text-xs">{reprice ? `Unpaid ones take the ${target ? cedi(target.fee) : "new"} fee. Paid and part-paid keep their current amount.` : "Off: every moved registration keeps its current amount."}</span>
    </span>
  </label>
);

export const MoveLegacyPanel = ({ eventId, savedEvent, dirty, onMoved }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");
  const [targetId, setTargetId] = useState("");
  const [reprice, setReprice] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/events/${eventId}/legacy-categories`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [eventId]);
  useEffect(() => { load(); }, [load, savedEvent]);
  const targets = FIXED_CATEGORIES.filter(([, field]) => savedEvent?.[field]?.enabled).map(([id, field, name]) => ({ id, name, fee: savedEvent[field].fee || 0 }));
  const src = rows.find((r) => r.id === source);
  const target = targets.find((t) => t.id === targetId);
  if (!rows.length) { return null; }
  const start = () => { setSource(rows[0].id); setTargetId(""); setReprice(false); setOpen(true); };
  const move = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/legacy-categories/move`, { source_id: source, target_id: targetId, reprice_unpaid: reprice });
      toast.success(`${r.data.moved} registration${r.data.moved === 1 ? "" : "s"} moved to ${r.data.target}`);
      setOpen(false);
      await onMoved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3" data-testid="move-legacy-panel">
      <BrandButton variant="outline" onClick={start} disabled={dirty} data-testid="move-legacy-open-button"><ArrowRightLeft className="w-4 h-4" />Move registrations to a fixed category</BrandButton>
      {dirty && <p className="text-xs text-amber-700 mt-1" data-testid="move-legacy-save-first">Save your changes first to move registrations.</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="move-legacy-dialog">
          <DialogHeader>
            <DialogTitle>Move registrations to a fixed category</DialogTitle>
            <DialogDescription>Everyone in the older category moves in one go. The older category is then removed from this event. Payments, receipts and tickets are not changed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold mb-1">Older category</div>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="move-legacy-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>{rows.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`move-legacy-source-${r.id}`}>{r.name} · {r.registrations} registration{r.registrations === 1 ? "" : "s"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs font-semibold mb-1">Move to</div>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid="move-legacy-target-select"><SelectValue placeholder={targets.length ? "Choose a fixed category" : "Switch on a fixed category and save first"} /></SelectTrigger>
                <SelectContent>{targets.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`move-legacy-target-${t.id}`}>{t.name} · {cedi(t.fee)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <PricingChoice reprice={reprice} setReprice={setReprice} target={target} />
            {src && <p className="text-xs text-gray-600" data-testid="move-legacy-summary">{src.registrations} registration{src.registrations === 1 ? "" : "s"} will move ({src.unpaid} unpaid).</p>}
          </div>
          <DialogFooter>
            <BrandButton variant="outline" onClick={() => setOpen(false)} data-testid="move-legacy-cancel">Cancel</BrandButton>
            <BrandButton onClick={move} disabled={busy || !source || !targetId} data-testid="move-legacy-confirm">{busy ? "Moving…" : "Move now"}</BrandButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
